<script setup lang="ts">
import { usePaneHeight } from '@/composeables/use-pane-height';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { provideFileManagerContextMenu } from './logic/core/useFileManagerContextMenu';
import { provideFileManagerCopy } from './logic/core/useFileManagerCopy';
import { provideFileManagerCreateFolder } from './logic/core/useFileManagerCreateFolder';
import { provideFileManagerDelete } from './logic/core/useFileManagerDelete';
import { provideFileManagerDetails } from './logic/core/useFileManagerDetails';
import { provideFileManagerMailDraft } from './logic/core/useFileManagerMailDraft';
import {
  provideFileManagerMounts,
  type FileManagerMount,
} from './logic/core/useFileManagerMounts';
import { provideFileManagerNavigation } from './logic/core/useFileManagerNavigation';
import { provideFileManagerPreview } from './logic/core/useFileManagerPreview';
import { provideFileManagerRename } from './logic/core/useFileManagerRename';
import { provideFileManagerSettings } from './logic/core/useFileManagerSettings';
import { useNavigationPathGuard } from './logic/core/useNavigationPathGuard';
import { provideDragPreviewRef } from './logic/drag-drop/useDragPreviewRef';
import { provideFileManagerDrag } from './logic/drag-drop/useFileManagerDrag';
import { provideFileExplorerSelection } from './logic/selection/useFileManagerSelection';
import { provideFileManagerTransfer } from './logic/transfer/useFileManagerTransfer';
import { provideFileManagerUpload } from './logic/upload/useFileManagerUpload';
import { fileManagerPermissionsFor } from './permissions/useFileManagerPermissions';
import { Container } from './sub-components/container';
import { ContextMenu } from './sub-components/context-menu';
import { CopyModal } from './sub-components/copy-modal';
import { DeleteConfirmModal } from './sub-components/delete-confirm-modal';
import { DragPreview } from './sub-components/drag-preview';
import { Header } from './sub-components/header';
import { MailDraftModal } from './sub-components/mail-draft-modal';
import { PreviewModal } from './sub-components/preview-modal';
import { RenameModal } from './sub-components/rename-modal';
import { TransferConfirmModal } from './sub-components/transfer-confirm-modal';
import { ViewLayout } from './sub-components/view-layout';
import type { PoolView, SidebarView } from './sub-components/view-layout/types';
import type { FileSystemItem } from './types';
import { isAtRoot, normalizeVirtualPath } from './utils';
import { DetailsView } from './views/details-view';
import { DirectoryListView } from './views/directory-list-view';
import { FileExplorerView } from './views/file-explorer-view';
import { NewFolderModal } from './views/file-explorer-view/sub-components/file-explorer-handler/sub-components/new-folder-modal';

// The one inbound prop the whole component is built around, and the only one (D4): the host owns the
// sites query, the ledger→spec join, prefix construction, key uniqueness, capability computation and
// the probe, and only renders this component once it holds a non-empty table. Nothing below knows what
// a project is — `route.query.path` is the last outside coupling left, and it is about navigation, not
// about projects (D9).
const props = defineProps<{
  mounts: FileManagerMount[];
}>();

const { paneRef, paneHeight } = usePaneHeight();

provideDragPreviewRef();
provideFileExplorerSelection();
// Return value unused here: layout/icon-size/view-mode are only ever read further down the tree via
// useFileManagerSettings().
provideFileManagerSettings();
const contextMenu = provideFileManagerContextMenu();
const { detailsItem, closeDetails } = provideFileManagerDetails();
const route = useRoute();
const router = useRouter();
// `path`/`navigate` are the only place in the whole file-manager tree that talk to vue-router
// directly — provideFileManagerNavigation and everything downstream of it (including
// useNavigationPathGuard below) receive them as plain injected values, so they stay
// router-agnostic and testable with plain refs.
const path = computed(() => normalizeVirtualPath(route.query.path));
const navigate = (path: string, opts?: { replace?: boolean }) => {
  const query = { ...route.query, path: isAtRoot(path) ? undefined : path };
  void (opts?.replace ? router.replace({ query }) : router.push({ query }));
};
const navigation = provideFileManagerNavigation(path, navigate);
// A pure adapter over the prop — no query, no probe, no status: see provideFileManagerMounts.
const mounts = provideFileManagerMounts(() => props.mounts);
// Not provided: capabilities ride on the mount table, which already is (D6). This is the same
// `permissionsAt` every descendant gets from useFileManagerPermissions(), built from the state this
// setup() holds directly — a component can never inject its own provide().
const permissions = fileManagerPermissionsFor(mounts);
// Redirects a stale/unresolvable deep-linked path back to root — see useNavigationPathGuard.ts. Wired
// here, after both navigation and mounts exist, for the same cross-injection reason as the composables
// below.
useNavigationPathGuard(navigation, mounts);
const { isPreviewOpen, closePreview } = provideFileManagerPreview();
// `mounts` is passed explicitly to the three composables created in this same setup(), for the
// cross-injection reason spelled out below — it is provided here, so they cannot inject it.
const createFolder = provideFileManagerCreateFolder(navigation, mounts);
const upload = provideFileManagerUpload(navigation, mounts);
// Destructured into setup scope, NOT read as `upload.fileInputRef` in the template, and not cosmetic:
// `<script setup>` resolves `ref="fileInputRef"` against setup BINDINGS at compile time. With no
// binding of that name it emits a plain string ref, which lands on `$refs` — somewhere nothing here
// reads — leaving the composable's ref undefined forever and `fileInputRef.value?.click()` a silent
// no-op. That is exactly how both file pickers stopped opening when these refs moved into the
// composable. Renaming either side without renaming the other brings the bug straight back.
const { fileInputRef, folderInputRef } = upload;
provideFileManagerDrag();
const transfer = provideFileManagerTransfer(navigation, mounts);
// `permissions`/`detailsItem`/`closeDetails` are passed explicitly rather than self-injected inside

const fileManagerDelete = provideFileManagerDelete(navigation, permissions, {
  detailsItem,
  closeDetails,
});

const fileManagerRename = provideFileManagerRename(navigation, permissions, {
  detailsItem,
});

const fileManagerCopy = provideFileManagerCopy(permissions, mounts);

const { isOpen: isMailDraftOpen } = provideFileManagerMailDraft(permissions);

const { currentPath } = navigation;
const {
  isOpen: isContextMenuOpen,
  position: contextMenuPosition,
  items: contextMenuItems,
} = contextMenu;

const { isModalOpen: isCreateFolderModalOpen } = createFolder;

const { isConfirmOpen: isDeleteConfirmOpen, pendingTarget: deleteTarget } =
  fileManagerDelete;

const { isModalOpen: isRenameModalOpen, initialName: renameInitialName } =
  fileManagerRename;

const { isModalOpen: isCopyModalOpen, initialName: copyInitialName } =
  fileManagerCopy;

const { pendingTransfer } = transfer;

const sidebar: SidebarView = { component: DirectoryListView };

const onOpenInSharePoint = (item: FileSystemItem) => {
  if (item.webUrl) window.open(item.webUrl, '_blank');
};

const poolViews = computed<PoolView[]>(() => {
  const isDetailsOpen = detailsItem.value !== null;
  return [
    { id: 'file-explorer-view', component: FileExplorerView },
    ...(isDetailsOpen
      ? [
          {
            id: 'details-view',
            component: DetailsView,
            props: { onOpenInSharepoint: onOpenInSharePoint },
          },
        ]
      : []),
  ];
});
</script>

<template>
  <div
    ref="paneRef"
    class="file-manager"
    :style="{ height: `${paneHeight}px` }"
    @dragover.prevent
    @drop.prevent
  >
    <Container>
      <Header :path="currentPath" />
      <ViewLayout :sidebar="sidebar" :views="poolViews" />
    </Container>
    <input
      ref="fileInputRef"
      type="file"
      multiple
      hidden
      @change="upload.onFileSelect"
    />
    <input
      ref="folderInputRef"
      type="file"
      multiple
      webkitdirectory
      hidden
      @change="upload.onFolderSelect"
    />
    <NewFolderModal
      v-if="isCreateFolderModalOpen"
      @close="isCreateFolderModalOpen = false"
      @create="createFolder.createFolder"
    />
    <RenameModal
      v-if="isRenameModalOpen"
      :initial-name="renameInitialName"
      @close="fileManagerRename.cancelRename"
      @rename="fileManagerRename.submitRename"
    />
    <CopyModal
      v-if="isCopyModalOpen"
      :initial-name="copyInitialName"
      @close="fileManagerCopy.cancelCopy"
      @copy="fileManagerCopy.submitCopy"
    />
    <ContextMenu
      v-if="isContextMenuOpen"
      :position="contextMenuPosition"
      :items="contextMenuItems"
      @close="contextMenu.close"
    />
    <PreviewModal v-if="isPreviewOpen" @close="closePreview" />
    <DeleteConfirmModal v-if="isDeleteConfirmOpen" :target="deleteTarget" />
    <TransferConfirmModal v-if="pendingTransfer" :transfer="pendingTransfer" />
    <MailDraftModal v-if="isMailDraftOpen" />
    <DragPreview />
  </div>
</template>

<style scoped>
.file-manager :deep(.card) {
  height: 100%;
}
</style>
